import { useEffect, useRef, useState } from 'react'

import { useGuardDispatch } from '../../../../shared/hooks/useGuardDispatch'
import { useUploadAudioFile } from '../../../../shared/hooks/useUploadAudioFile'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'
import { useRecorder } from 'use-recorder'

import {
  Box,
  Button,
  MultiSelectInput,
  MultilineTextInput,
  NoteIcon,
  PauseIcon,
  PlayIcon,
  RecordIcon,
  StopIcon,
  VoiceMemoIcon,
  Waveform,
} from '@hivewatch/dark'

import {
  IncidentDocument,
  IncidentQueryResult,
  useCreateAudioMessageMutation,
} from '@/graphql/generated/hooks'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { formatDate } from '@/utils/formatDateTime'

import { MixpanelDataIProps } from '../../../../shared/types/types'
import { NoAvailableGuardsAlert } from './NoAvailableGuardsAlert'

type ColorScheme = 'dark' | 'light'
type GuardPanelMode = 'dispatch' | 'radio'

interface GuardPanelIProps {
  incident?: IncidentQueryResult['data']['incident']
  mixpanelData?: MixpanelDataIProps
  onActionComplete?: () => void
  colorScheme?: ColorScheme
  mode?: GuardPanelMode
}

export const GuardPanel = ({
  incident,
  mixpanelData,
  onActionComplete,
  colorScheme = 'dark',
  mode = 'dispatch',
}: GuardPanelIProps) => {
  const { t } = useTranslation()
  const [isVoiceOpen, setIsVoiceOpen] = useState(false)
  const [isNoteOpen, setIsNoteOpen] = useState(false)

  // Voice recorder state
  const wavesurfer = useRef(null)
  // The mic preflight in handleRecord awaits browser chrome that can outlive
  // the panel; the continuation must not start a recording after unmount.
  const isMountedRef = useRef(true)
  // Synchronous re-entry guard: the permission prompt keeps the record
  // controls enabled while handleRecord awaits, so without this a rapid
  // second click would launch a concurrent getUserMedia + start() pair.
  const isPreflightPendingRef = useRef(false)
  // Tracks whether use-recorder delivered a clip after stop(); the stop
  // watchdog uses it to unwedge the send gate if the recorder's own
  // (swallowed) acquisition failed after a successful preflight.
  const playerArrivedRef = useRef(false)
  // The single live stop-watchdog; owning one id (cancelled on reschedule,
  // player arrival, and unmount) keeps a stale timer from an earlier
  // recording from clearing the gate during a later one's finalization.
  const watchdogRef = useRef<number | null>(null)
  const clearWatchdog = () => {
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
  }
  useEffect(
    () => () => {
      isMountedRef.current = false
      clearWatchdog()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReset, setIsReset] = useState(false)
  const [recorderStatus, setRecorderStatus] = useState<
    'paused' | 'recording' | 'playing' | 'silent'
  >('paused')
  const { start, stop, player, audio } = useRecorder()

  const {
    audioFile,
    note,
    onNoteChange,
    onClearAudioFile,
    onGenerateAudioFile,
    isLoading,
    setIsRecording,
    selectedIds,
    guardsOptions,
    toggle,
    onClear,
    onCancel,
    isDispatchDisabled,
    onDispatch,
    hasAvailableGuards,
    hasCheckedInGuards,
  } = useGuardDispatch({ incident, mixpanelData, onActionComplete })

  // Radio guards mode
  const isRadio = mode === 'radio'
  const { shouldEnableIncidentRadioGuards } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const [radioFile, setRadioFile] = useState<File | null>(null)
  const [isRadioLoading, setRadioLoading] = useState(false)
  const uploadAudioFile = useUploadAudioFile()
  const [createAudioMessage] = useCreateAudioMessageMutation()

  const onRadioSubmit = async () => {
    const facilityId = incident?.facility?.id
    const incidentId = incident?.id
    if (!radioFile || !facilityId || !incidentId) return
    setRadioLoading(true)
    try {
      const audioClipId = (await uploadAudioFile({
        file: radioFile,
        facilityId,
        incidentId,
      })) as string
      const { data: audioMessageData } = await createAudioMessage({
        variables: {
          input: { facilityId, incidentId, audioClipId },
        },
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
        ],
      })
      showSuccess(t('incidents:audio_message_created'))
      trackEvent(`${BUTTON_PRESS} Radio Guards - Send Message`, {
        ...mixpanelData,
        // snake_case per the analytics rule, matching the currently active
        // DarkGuardPanel's property (the legacy camelCase twin is drift).
        audio_message_id:
          audioMessageData?.createAudioMessage?.audioMessage?.id,
      })
      setRadioFile(null)
      onActionComplete?.()
    } catch (error) {
      Sentry.captureException(error)
      showError()
    } finally {
      setRadioLoading(false)
    }
  }

  const onRadioCancel = () => {
    trackEvent(`${BUTTON_PRESS} Radio Guards - Cancel Button`, mixpanelData)
    onActionComplete?.()
  }

  // When a recording finishes, store the audio file and clear reset flag
  const [recordingSrc, setRecordingSrc] = useState<string | null>(null)

  useEffect(() => {
    if (player) {
      playerArrivedRef.current = true
      clearWatchdog()
      if (isRadio) {
        const blob = (audio as { blob?: Blob })?.blob
        const emptyBlob = new Blob([], { type: 'audio/wav' })
        setRadioFile(
          new File([blob || emptyBlob], new Date().valueOf().toString(), {
            type: 'audio/wav',
          })
        )
      } else {
        onGenerateAudioFile(audio as unknown as { blob: BlobPart })
      }
      setIsRecording(false)
      setIsReset(false)
      setRecordingSrc(player.src)
    }
  }, [player])

  const activeFile = isRadio ? radioFile : audioFile
  useEffect(() => {
    if (!activeFile && !isReset) {
      setRecorderStatus('silent')
      player?.pause()
      wavesurfer.current = null
      setRecordingSrc(null)
      setIsReset(true)
    }
  }, [activeFile, player])

  const handleRecord = async () => {
    if (isPreflightPendingRef.current) return
    isPreflightPendingRef.current = true
    // use-recorder's start() swallows its own getUserMedia rejection, so a
    // denied/missing mic gives no failure signal and the recording flags
    // would wedge the send button disabled. Acquire (and release) the mic
    // first; only flip the flags once recording can actually begin.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())
    } catch {
      if (isMountedRef.current) showError()
      return
    } finally {
      isPreflightPendingRef.current = false
    }
    // The permission prompt can stay open after the panel is closed; if it
    // was granted post-unmount there is no UI left to stop a recording, so
    // bail out (the preflight stream above is already released).
    if (!isMountedRef.current) return
    // Drop any previously stored clip: from here the old recording is hidden,
    // and if the replacement fails the send path must not upload it.
    if (isRadio) {
      setRadioFile(null)
    } else {
      onClearAudioFile()
    }
    wavesurfer.current = null
    setRecordingSrc(null)
    setIsPlaying(false)
    setIsReset(true)
    trackEvent(
      `${BUTTON_PRESS} ${isRadio ? 'Radio Guards' : 'Dispatch'} - Recording`,
      mixpanelData
    )
    setIsRecording(true)
    setRecorderStatus('recording')
    start()
  }

  const handleStopRecording = () => {
    setRecorderStatus('paused')
    // isRecording stays set until the player effect stores the finished
    // audio file — clearing it here would re-enable Dispatch while the clip
    // is still finalizing, and a fast click would submit without it.
    playerArrivedRef.current = false
    stop()
    // Watchdog: the preflight cannot guarantee the recorder's own separate
    // acquisition succeeded (it swallows failures). If no clip materializes
    // shortly after stopping, unwedge the send gate — with no audio coming,
    // sending without it is the correct outcome.
    clearWatchdog()
    watchdogRef.current = window.setTimeout(() => {
      watchdogRef.current = null
      if (!playerArrivedRef.current && isMountedRef.current) {
        setIsRecording(false)
      }
    }, 4000)
  }

  const handlePlayPause = () => {
    try {
      if (!wavesurfer.current) return
      wavesurfer.current.playPause()
      setIsPlaying(!isPlaying)
    } catch {
      // wavesurfer may be in an invalid state
    }
  }

  const hasRecording = !!player && !isReset
  const isRecordingActive = recorderStatus === 'recording'
  const isRadioSendDisabled =
    !radioFile ||
    isRadioLoading ||
    !shouldEnableIncidentRadioGuards ||
    isRecordingActive

  const isDark = colorScheme === 'dark'
  const labelColor = isDark ? 'text.primary' : 'light.text.primary'
  const subtitleColor = isDark ? 'text.secondary' : 'light.text.tertiary'
  const descColor = isDark ? 'text.secondary' : 'light.text.primary'

  const toggleButtonProps = (isActive: boolean) =>
    isDark
      ? {
          borderColor: isActive
            ? 'interactive.focus-ring'
            : 'interactive.tertiary-border',
          ...(isActive ? { bg: 'interactive.tertiary-hover-bg' } : {}),
        }
      : {
          ...(isActive ? { bg: 'light.interactive.tertiary-hover-bg' } : {}),
        }

  const iconButtonBase = {
    alignItems: 'center' as const,
    as: 'button' as const,
    border: '1px solid',
    borderColor: isDark ? 'border.subtle' : 'light.border.subtle',
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    h: '36px',
    justifyContent: 'center',
    transition:
      'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    w: '36px',
  }

  const iconButtonHover = {
    bg: isDark
      ? 'interactive.tertiary-hover-bg'
      : 'light.interactive.tertiary-hover-bg',
    borderColor: isDark ? 'interactive.tertiary-hover-border' : undefined,
    color: isDark ? 'icon.primary' : undefined,
  }

  const iconButtonActive = {
    bg: isDark ? 'bg.tab-active' : 'light.interactive.tertiary-hover-bg',
    color: isDark ? 'icon.active' : undefined,
  }

  const activeBg = isDark
    ? 'bg.tab-active'
    : 'light.interactive.tertiary-hover-bg'

  return (
    <Box display='flex' flexDirection='column' gap='16px' px='24px' py='16px'>
      {/* Header */}
      <Box display='flex' flexDirection='column' gap='2px'>
        <Box
          color={labelColor}
          fontFamily="'Soehne Kraftig', sans-serif"
          fontSize='20px'
          fontWeight='500'
          lineHeight='28px'
        >
          {isRadio
            ? t('incidents:radio_guards')
            : t('incidents:dispatch_guards')}
        </Box>
        <Box
          color={subtitleColor}
          fontFamily="'Soehne', sans-serif"
          fontSize='11px'
          lineHeight='16px'
        >
          {formatDate(new Date())}
        </Box>
      </Box>

      <NoAvailableGuardsAlert
        colorScheme={colorScheme}
        hasGuards={isRadio ? hasCheckedInGuards : hasAvailableGuards}
        mixpanelData={mixpanelData}
      />

      {/* Guard multi-select (dispatch mode only) */}
      {!isRadio && (
        <MultiSelectInput
          colorScheme={colorScheme}
          intentLabel={t('incidents:dispatch_guards_search_header')}
          label={t('incidents:dispatch_guards_label')}
          maxDropdownHeight={240}
          noMatchLabel={t('incidents:dispatch_no_guards_found')}
          onClear={onClear}
          onRemove={toggle}
          onToggleOption={toggle}
          options={guardsOptions}
          placeholder={t('incidents:dispatch_guards_multi_placeholder')}
          resultsHeader={t('incidents:dispatch_guards_select_header')}
          searchable
          selected={selectedIds}
        />
      )}

      {/* Voice/note label */}
      <Box
        color={descColor}
        fontFamily="'Soehne', sans-serif"
        fontSize='12px'
        fontWeight='500'
        letterSpacing='0.96px'
        lineHeight='16px'
        textTransform='uppercase'
      >
        {t('incidents:incident_drawer_dispatch_message_label')}
      </Box>

      {/* Voice + note buttons */}
      {isVoiceOpen ? (
        <Box alignItems='center' display='flex' gap='8px'>
          {/* Record / Stop button */}
          <Box
            {...iconButtonBase}
            _active={iconButtonActive}
            _hover={iconButtonHover}
            onClick={isRecordingActive ? handleStopRecording : handleRecord}
          >
            {isRecordingActive ? (
              <StopIcon size={14} />
            ) : (
              <RecordIcon size={14} />
            )}
          </Box>

          {/* Play / Pause button (visible after recording) */}
          {hasRecording && !isRecordingActive && (
            <Box
              {...iconButtonBase}
              _active={isPlaying ? {} : iconButtonActive}
              _hover={isPlaying ? {} : iconButtonHover}
              bg={isPlaying ? activeBg : undefined}
              color={
                isPlaying
                  ? isDark
                    ? 'icon.active'
                    : 'light.icon.secondary'
                  : isDark
                  ? 'icon.secondary'
                  : 'light.icon.secondary'
              }
              onClick={handlePlayPause}
            >
              {isPlaying ? (
                <PauseIcon color='currentColor' size={14} />
              ) : (
                <PlayIcon color='currentColor' size={14} />
              )}
            </Box>
          )}

          {/* Prompt / Status / Waveform area */}
          {hasRecording && !isRecordingActive ? (
            <Waveform
              colorScheme={colorScheme}
              onFinish={() => setIsPlaying(false)}
              onReady={(ws) => {
                wavesurfer.current = ws
              }}
              src={recordingSrc}
            />
          ) : (
            <Box
              alignItems='center'
              bg={isDark ? 'bg.input' : 'light.bg.input'}
              cursor={!isRecordingActive ? 'pointer' : 'default'}
              display='flex'
              flex='1'
              h='36px'
              minW='0'
              onClick={!isRecordingActive ? handleRecord : undefined}
              overflow='hidden'
              px='12px'
            >
              {isRecordingActive && (
                <Box
                  color='status.error'
                  fontFamily="'Soehne', sans-serif"
                  fontSize='16px'
                  lineHeight='24px'
                  whiteSpace='nowrap'
                >
                  {t('incidents:dispatch_recording')}
                </Box>
              )}
              {!isRecordingActive && !hasRecording && (
                <Box
                  color={isDark ? 'text.link' : 'light.text.link'}
                  fontFamily="'Soehne', sans-serif"
                  fontSize='16px'
                  lineHeight='24px'
                  whiteSpace='nowrap'
                >
                  {t('common:voice_recorder_start')}
                </Box>
              )}
            </Box>
          )}
          {/* Radio messages are voice-only — CreateAudioMessage carries no
              text, so the note affordance only exists for dispatch. */}
          {!isRadio && (
            <Box
              {...iconButtonBase}
              _active={isNoteOpen ? {} : iconButtonActive}
              _hover={isNoteOpen ? {} : iconButtonHover}
              bg={isNoteOpen ? activeBg : undefined}
              color={
                isNoteOpen && isDark
                  ? 'icon.active'
                  : isDark
                  ? 'icon.secondary'
                  : 'light.icon.secondary'
              }
              onClick={() => setIsNoteOpen((o) => !o)}
            >
              <NoteIcon color='currentColor' size={16} />
            </Box>
          )}
        </Box>
      ) : (
        <Box display='flex' gap='8px'>
          <Button
            colorScheme={colorScheme}
            {...toggleButtonProps(false)}
            leftIcon={
              <Box
                as='span'
                color={isDark ? 'icon.secondary' : 'light.icon.secondary'}
                display='inline-flex'
              >
                <VoiceMemoIcon color='currentColor' size={20} />
              </Box>
            }
            onClick={() => setIsVoiceOpen((o) => !o)}
            size='md'
            variant='secondary'
          >
            {t('incidents:dispatch_record_voice_message')}
          </Button>
          {!isRadio && (
            <Button
              colorScheme={colorScheme}
              {...toggleButtonProps(isNoteOpen)}
              leftIcon={
                <Box
                  as='span'
                  color={isDark ? 'icon.secondary' : 'light.icon.secondary'}
                  display='inline-flex'
                >
                  <NoteIcon color='currentColor' size={16} />
                </Box>
              }
              onClick={() => setIsNoteOpen((o) => !o)}
              size='md'
              variant='secondary'
            >
              {t('incidents:dispatch_add_note')}
            </Button>
          )}
        </Box>
      )}

      {/* Note input (dispatch only — radio messages are voice-only) */}
      {!isRadio && (
        <Box
          overflow='hidden'
          style={{
            maxHeight: isNoteOpen ? '200px' : '0px',
            marginTop: isNoteOpen ? '0px' : '-16px',
            transition: 'max-height 0.25s ease, margin-top 0.25s ease',
          }}
        >
          <MultilineTextInput
            colorScheme={colorScheme}
            onChange={onNoteChange}
            placeholder={t('incidents:dispatch_comment_placeholder')}
            value={note}
          />
        </Box>
      )}

      {/* Action row */}
      <Box display='flex' gap='8px' justifyContent='flex-end'>
        <Button
          colorScheme={colorScheme}
          onClick={isRadio ? onRadioCancel : onCancel}
          size='md'
          variant='secondary'
        >
          {t('common:cancel')}
        </Button>
        <Button
          colorScheme={colorScheme}
          disabled={isRadio ? isRadioSendDisabled : isDispatchDisabled}
          isLoading={isRadio ? isRadioLoading : isLoading}
          onClick={isRadio ? onRadioSubmit : onDispatch}
          size='md'
          variant='primary'
        >
          {isRadio
            ? t('incidents:send_message')
            : t('incidents:dispatch_action')}
        </Button>
      </Box>
    </Box>
  )
}
